import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Mail, MapPin, Phone } from "lucide-react";
import Footer from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
    name: "",
    company: "",
    email: "",
    phone: "",
    role: "",
    message: "",
    privacy_accepted: false,
};

export default function ContactFooter() {
    const [form, setForm] = useState(initial);
    const [status, setStatus] = useState({ loading: false, success: false, error: "" });

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, success: false, error: "" });
        if (!form.privacy_accepted) {
            setStatus({ loading: false, success: false, error: "Devi accettare la privacy policy per proseguire." });
            return;
        }
        try {
            await axios.post(`${API}/demo-requests`, form);
            setStatus({ loading: false, success: true, error: "" });
            setForm(initial);
        } catch (err) {
            const raw = err?.response?.data?.detail;
            let detail = "";
            if (typeof raw === "string") {
                detail = raw;
            } else if (Array.isArray(raw)) {
                detail = raw.map((d) => d.msg || d.detail || "").filter(Boolean).join(", ");
            }
            setStatus({
                loading: false,
                success: false,
                error: detail || "Si è verificato un errore. Riprova o scrivici a info@tecsuite.it.",
            });
        }
    };

    const inputClass =
        "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition";

    return (
        <>
            {/* CONTACT */}
            <section
                id="contatti"
                data-testid="contact-section"
                className="relative bg-white py-24 sm:py-28 lg:py-32"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
                                <span className="h-px w-8 bg-teal-500" />
                                Contatti
                            </span>
                            <h2
                                data-testid="contact-title"
                                className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900"
                            >
                                Richiedi una demo di TecSuite
                            </h2>
                            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                                Compila il modulo: ti ricontattiamo per approfondire i tuoi flussi e mostrarti la
                                piattaforma su un caso reale.
                            </p>

                            <div className="mt-10 space-y-4">
                                <div className="flex items-start gap-3">
                                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                                        <MapPin className="w-4.5 h-4.5" />
                                    </span>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">Sede</p>
                                        <p className="text-sm text-slate-600">
                                            Viale dell&apos;Industria, 11 — 37023 Grezzana (VR)
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                                        <Phone className="w-4.5 h-4.5" />
                                    </span>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">Telefono</p>
                                        <a
                                            href="tel:+390454770241"
                                            className="text-sm text-slate-600 hover:text-cyan-600"
                                            data-testid="contact-phone"
                                        >
                                            045 4770241
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                                        <Mail className="w-4.5 h-4.5" />
                                    </span>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">Email</p>
                                        <a
                                            href="mailto:info@tecsuite.it"
                                            className="text-sm text-slate-600 hover:text-cyan-600"
                                            data-testid="contact-email"
                                        >
                                            info@tecsuite.it
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-7">
                            <div className="rounded-3xl bg-gradient-to-br from-slate-50 to-white ring-1 ring-slate-200 p-6 sm:p-10 shadow-sm">
                                {status.success ? (
                                    <div
                                        data-testid="contact-success"
                                        className="flex flex-col items-center text-center py-10"
                                    >
                                        <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-100 text-teal-700 ring-1 ring-teal-200">
                                            <CheckCircle2 className="w-7 h-7" />
                                        </span>
                                        <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">
                                            Grazie, abbiamo ricevuto la tua richiesta.
                                        </h3>
                                        <p className="mt-3 text-slate-600 max-w-md">
                                            Ti ricontatteremo al più presto per approfondire e organizzare la demo di TecSuite.
                                        </p>
                                        <button
                                            onClick={() => setStatus({ loading: false, success: false, error: "" })}
                                            data-testid="contact-reset"
                                            className="mt-6 inline-flex items-center rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-5 py-2.5"
                                        >
                                            Invia un&apos;altra richiesta
                                        </button>
                                    </div>
                                ) : (
                                    <form
                                        onSubmit={onSubmit}
                                        data-testid="contact-form"
                                        className="grid gap-4 sm:grid-cols-2"
                                    >
                                        <div>
                                            <label htmlFor="name" className="block text-xs font-semibold text-slate-700 mb-1.5">
                                                Nome e cognome*
                                            </label>
                                            <input
                                                id="name"
                                                name="name"
                                                required
                                                value={form.name}
                                                onChange={onChange}
                                                data-testid="input-name"
                                                className={inputClass}
                                                placeholder="Mario Rossi"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="company" className="block text-xs font-semibold text-slate-700 mb-1.5">
                                                Azienda*
                                            </label>
                                            <input
                                                id="company"
                                                name="company"
                                                required
                                                value={form.company}
                                                onChange={onChange}
                                                data-testid="input-company"
                                                className={inputClass}
                                                placeholder="La tua azienda"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1.5">
                                                Email*
                                            </label>
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                required
                                                value={form.email}
                                                onChange={onChange}
                                                data-testid="input-email"
                                                className={inputClass}
                                                placeholder="mario@azienda.it"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="phone" className="block text-xs font-semibold text-slate-700 mb-1.5">
                                                Telefono
                                            </label>
                                            <input
                                                id="phone"
                                                name="phone"
                                                value={form.phone}
                                                onChange={onChange}
                                                data-testid="input-phone"
                                                className={inputClass}
                                                placeholder="+39 ..."
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label htmlFor="role" className="block text-xs font-semibold text-slate-700 mb-1.5">
                                                Ruolo
                                            </label>
                                            <input
                                                id="role"
                                                name="role"
                                                value={form.role}
                                                onChange={onChange}
                                                data-testid="input-role"
                                                className={inputClass}
                                                placeholder="Es. Titolare, Responsabile IT, Responsabile assistenza..."
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label htmlFor="message" className="block text-xs font-semibold text-slate-700 mb-1.5">
                                                Messaggio
                                            </label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                rows={4}
                                                value={form.message}
                                                onChange={onChange}
                                                data-testid="input-message"
                                                className={`${inputClass} resize-y`}
                                                placeholder="Raccontaci come gestisci oggi interventi, contratti e clienti..."
                                            />
                                        </div>
                                        <div className="sm:col-span-2 flex items-start gap-3 mt-1">
                                            <input
                                                id="privacy_accepted"
                                                name="privacy_accepted"
                                                type="checkbox"
                                                checked={form.privacy_accepted}
                                                onChange={onChange}
                                                data-testid="input-privacy"
                                                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                                            />
                                            <label htmlFor="privacy_accepted" className="text-[13px] text-slate-600 leading-relaxed">
                                                Ho letto e accettato la{" "}
                                                <a href="/privacy" className="text-cyan-700 font-semibold hover:underline">
                                                    Privacy Policy
                                                </a>{" "}
                                                e autorizzo il trattamento dei dati per essere ricontattato.
                                            </label>
                                        </div>
                                        {status.error && (
                                            <div
                                                data-testid="contact-error"
                                                className="sm:col-span-2 rounded-lg bg-rose-50 ring-1 ring-rose-200 px-4 py-3 text-sm text-rose-700"
                                            >
                                                {status.error}
                                            </div>
                                        )}
                                        <div className="sm:col-span-2 flex items-center justify-between mt-2">
                                            <p className="text-xs text-slate-500">
                                                *Campi obbligatori
                                            </p>
                                            <button
                                                type="submit"
                                                disabled={status.loading}
                                                data-testid="contact-submit"
                                                className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-3 transition"
                                            >
                                                {status.loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                                Invia richiesta
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <Footer />
        </>
    );
}
